import React from "react";

const DEFAULT_BRAND = "#10B981";

const PBlock = ({ block }) => (
  <p className="text-[13px] leading-relaxed text-slate-700 whitespace-pre-wrap">{block.text}</p>
);

const SubheadingBlock = ({ block }) => (
  <h4 className="text-sm font-semibold text-slate-900 mt-4 mb-1">{block.text}</h4>
);

const KvBlock = ({ block }) => (
  <dl className="space-y-2">
    {block.items.map((it) => (
      <div key={it.k} className="grid grid-cols-[180px_1fr] gap-3">
        <dt className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 pt-0.5">{it.k}</dt>
        <dd className="text-[13px] text-slate-800 whitespace-pre-wrap">{it.v}</dd>
      </div>
    ))}
  </dl>
);

const CalloutBlock = ({ block, brandColor }) => (
  <div className="my-3 px-4 py-3" style={{ borderLeft: `4px solid ${brandColor}`, background: `${brandColor}14` }}>
    <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: brandColor }}>{block.label}</p>
    <p className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{block.value}</p>
  </div>
);

const ChecklistBlock = ({ block, brandColor }) => {
  if (!block.items.length) return <p className="text-[13px] italic text-slate-400">No items.</p>;
  return (
    <ul className="space-y-1">
      {block.items.map((it) => (
        <li key={it.text} className="text-[13px] text-slate-700 flex items-center gap-2">
          <span className="inline-flex h-4 w-4 items-center justify-center rounded border text-[10px] text-white" style={{ background: it.done ? brandColor : "transparent", borderColor: it.done ? brandColor : "#94a3b8" }}>{it.done ? "✓" : ""}</span>
          <span className={it.done ? "line-through text-slate-400" : ""}>{it.text}</span>
        </li>
      ))}
    </ul>
  );
};

const LinksBlock = ({ block, brandColor }) => {
  if (!block.items.length) return <p className="text-[13px] italic text-slate-400">No supporting materials.</p>;
  return (
    <ul className="space-y-1">
      {block.items.map((it) => (
        <li key={it.url || it.label} className="text-[13px]">
          {it.url ? (
            <a href={it.url} target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline break-all" style={{ color: brandColor }}>{it.label || it.url}</a>
          ) : (
            <span className="text-slate-700">{it.label}</span>
          )}
        </li>
      ))}
    </ul>
  );
};

const TableBlock = ({ block }) => (
  <table className="w-full text-[12px] border-collapse my-1">
    <thead>
      <tr>
        {block.columns.map((c) => (
          <th key={c} className="text-left font-semibold text-slate-600 border-b-2 border-slate-300 px-2 py-1.5 bg-slate-50">{c}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {block.rows.map((r, ri) => (
        <tr key={`${ri}-${r[0]}`} className="border-b border-slate-200">
          {r.map((cell, ci) => (
            <td key={block.columns[ci]} className="px-2 py-1.5 text-slate-700 align-top whitespace-pre-wrap">{cell}</td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

const BLOCK_COMPONENTS = {
  p: PBlock,
  subheading: SubheadingBlock,
  kv: KvBlock,
  callout: CalloutBlock,
  checklist: ChecklistBlock,
  links: LinksBlock,
  table: TableBlock,
};

export function Block({ block, brandColor = DEFAULT_BRAND }) {
  const Comp = BLOCK_COMPONENTS[block.type];
  return Comp ? <Comp block={block} brandColor={brandColor} /> : null;
}

// The 8.5x11 corporate document page — shared by the preview modal and the public review portal.
export default function DocumentView({ model, brandColor = DEFAULT_BRAND, agencyName = "Performance Flow Pro", id = "print-area" }) {
  return (
    <div id={id} className="doc-page shadow-2xl" style={{ padding: "0.75in" }}>
      {model.cover ? (
        <div className="pb-8 mb-2 border-b border-slate-200">
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              {model.meta.agency_logo && <img src={model.meta.agency_logo} alt="Agency logo" data-testid="deck-cover-agency-logo" className="h-12 w-auto max-w-[180px] object-contain mb-4" />}
              <p className="text-[11px] uppercase tracking-[0.3em] font-semibold text-slate-500" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{agencyName}</p>
              <div className="h-[5px] w-20 rounded mt-4 mb-6" style={{ background: brandColor }} />
              <h1 className="text-4xl font-extrabold text-slate-900 leading-tight">{model.meta.company_name || model.title}</h1>
              <p className="text-base text-slate-600 mt-2 font-medium">{model.meta.business_unit || model.subtitle}</p>
            </div>
            {model.meta.logo && (
              <img src={model.meta.logo} alt="Company logo" data-testid="deck-cover-logo" className="h-20 w-auto max-w-[180px] object-contain flex-shrink-0" />
            )}
          </div>
          <div className="mt-6 grid grid-cols-3 gap-4 border-t border-slate-200 pt-4 text-sm">
            <div><p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Prepared for</p><p className="font-semibold text-slate-700 mt-0.5">{model.meta.project}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Date</p><p className="font-semibold text-slate-700 mt-0.5">{model.meta.date}</p></div>
            <div><p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Classification</p><p className="font-semibold text-slate-700 mt-0.5">Confidential</p></div>
          </div>
          {(model.toc || []).length > 0 && (
            <div className="mt-8" data-testid="deck-toc">
              <h2 className="text-lg font-bold text-slate-900 border-b-2 pb-1.5 mb-3" style={{ borderColor: brandColor }}>Contents</h2>
              <ol className="space-y-0">
                {model.toc.map((t, i) => (
                  <li key={`toc-${i}`} className="flex items-baseline gap-3 py-2 border-b border-slate-100">
                    <span className="text-sm font-extrabold text-slate-900">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[13px] font-semibold text-slate-700">{t}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-start justify-between gap-6 pb-4" style={{ borderBottom: `2px solid ${brandColor}` }}>
          <div className="min-w-0">
            {model.meta.agency_logo && <img src={model.meta.agency_logo} alt="Agency logo" data-testid="report-agency-logo" className="h-10 w-auto max-w-[150px] object-contain mb-2" />}
            <p className="text-[11px] uppercase tracking-[0.25em] font-semibold text-slate-500" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{agencyName}</p>
            <h1 className="text-2xl font-bold text-slate-900 mt-1">{model.title}</h1>
            <p className="text-xs text-slate-500 mt-1">{model.subtitle}</p>
          </div>
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            {model.meta.logo && <img src={model.meta.logo} alt="Company logo" data-testid="report-company-logo" className="h-12 w-auto max-w-[150px] object-contain" />}
            <div className="text-right text-[11px] text-slate-500 leading-relaxed">
              <p className="font-semibold text-slate-700">{model.meta.project}</p>
              <p>{model.meta.date}</p>
              <p className="mt-1">Confidential</p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-6">
        {model.sections.map((sec, sIdx) => (
          <section key={`${sIdx}-${sec.heading}`} className={sec.part ? "doc-section pt-4" : "doc-section"}>
            <h2
              className={sec.part ? "text-lg font-extrabold text-slate-900 pb-1.5 mb-3 border-b-2" : "text-base font-bold text-slate-900 border-b border-slate-200 pb-1 mb-3"}
              style={sec.part ? { borderColor: brandColor } : undefined}
            >
              {sec.heading}
            </h2>
            <div className="space-y-2">
              {sec.blocks.map((b, bi) => <Block key={`${b.type}-${bi}`} block={b} brandColor={brandColor} />)}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-10 pt-3 border-t border-slate-200 text-[10px] text-slate-400 flex justify-between">
        <span>Generated by {agencyName} · ATD 6-Phase HPI</span>
        <span>{model.meta.project}</span>
      </div>
    </div>
  );
}
